import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  StatusDot,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import {
  ANNOUNCEMENT_AUDIENCE_RULE_INFO,
  GAME_REASON_SEPARATOR,
  type AnnouncementAudienceRuleKey,
} from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { ComposeAnnouncementDialog } from './ComposeAnnouncementDialog.tsx';
import { RewriteCopyDialog, type DraftCopy } from './RewriteCopyDialog.tsx';
import { AudienceSummary, type AudienceCount } from './AudienceSummary.tsx';
import { notificationState } from './notification-state.ts';
import {
  NotificationSwitchDialog,
  type NotificationSetting,
} from './NotificationSwitchDialog.tsx';

/* ── The shapes §19's routes actually return ───────────────────────────────
   snake_case, straight off the SELECT lists in services/api/src/modules/
   backoffice/platform.ts.

   Six routes back this screen. Two read:
     GET   /api/platform/announcements
     GET   /api/platform/notifications
   two move a queued send:
     POST  /api/platform/announcements/:id/schedule
     POST  /api/platform/announcements/:id/cancel
   and two COMPOSE, which is new — `platform.announcement` had no create path
   at all, so the schedule route's refusal for a missing locale named a remedy
   the platform did not have:
     POST  /api/platform/announcements          (platform.manage)
     PATCH /api/platform/announcements/:id      (platform.manage, copy only)

   and three that make an announcement reach somebody — which is new, and is
   the gap that had been misreported for three rounds as "blocked on the
   mailer". The mailer was never the problem; the AUDIENCE RESOLVER was:
     POST  /api/platform/announcements/audience/preview   (platform.manage)
     GET   /api/platform/announcements/:id/audience       (access.manage)
     POST  /api/platform/announcements/:id/send           (access.manage)
     GET   /api/platform/announcements/deliveries         (access.manage)

   `audience_rule` is a closed vocabulary now, `platform.announcement_delivery`
   records one row per person, and the Delivery report reads the outbox rather
   than saying nothing records a send. What it still cannot say is opened or
   tapped: this platform has no open tracking of any kind, and email is the
   only channel with a transport. Both are stated on screen. */

interface AnnouncementRow {
  id: string;
  /** AN-#### — the platform-wide public reference. */
  ref: string;
  /**
   * MANDATORY · PROMOTIONAL. Not decoration: the column comment on
   * platform.announcement.kind IS the rule — mandatory notices ignore marketing
   * opt-out, promotional ones never do. Two announcements with identical
   * audiences reach different players depending on this one word.
   */
  kind: string;
  /** DRAFT · SCHEDULED · LIVE · SENT */
  status: string;
  /**
   * { 'en-PH': string, 'fil-PH': string }. The player-facing TITLE lives in
   * here — platform.announcement has no title column at all — so the list
   * cannot be sorted or searched on it without a jsonb expression index.
   */
  copy: Record<string, unknown> | null;
  /** ['IN_APP','EMAIL','PUSH'] */
  channels: string[] | null;
  /**
   * `{ rule, description }` since the vocabulary landed, where `description` is
   * DERIVED from the rule server-side. Rows composed before it — the five the
   * seed wrote — carry a `description` and no `rule`, and they can be read and
   * cannot be sent: nothing can turn "VIP 3 and above" into a set of people.
   */
  audience_rule: { rule?: string; description?: string } | null;
  scheduled_for: string | null;
  sent_at: string | null;
  created_at: string;
  brand: string | null;
  created_by: string | null;
  /** `(copy ? 'en-PH')::text` — Postgres casts the boolean, so this is 'true'/'false'. */
  has_english: string;
  has_filipino: string;
}

/**
 * One announcement's delivery, aggregated from `platform.announcement_delivery`
 * joined to `platform.mail_message`.
 *
 * Every figure here is a MESSAGE STATUS moved by the dispatcher. There is no
 * opened and no tapped column, because this platform has no open tracking at
 * all — no pixel, no click wrapper, no SES event subscription — and a zero in
 * such a column would read as "nobody opened it" rather than as "nobody
 * measured it".
 */
interface DeliveryRow {
  id: string;
  ref: string;
  kind: string;
  status: string;
  brand: string | null;
  sent_at: string | null;
  recipients: number;
  delivered: number;
  in_flight: number;
  retrying: number;
  suppressed: number;
  dead: number;
  first_recorded_at: string;
}

interface NotificationRow {
  key: string;
  label: string;
  description: string;
  channels: string[] | null;
  /**
   * Mandatory by licence — a player must be told when a restriction lands on
   * their account. The design drew a switch here too; this one is deliberately
   * not offered, and the row says why in visible text.
   * `710_notification_setting_writes.sql` puts the same rule in a CHECK.
   */
  mandatory: boolean;
  enabled: boolean;
  /**
   * The last move on this row, off `platform.notification_setting_change`.
   *
   * Null on a setting nobody has ever switched, which is a DIFFERENT fact from
   * one switched off and back on again — so the column says "never changed"
   * rather than leaving a blank that reads as missing data. `last_reason` is
   * packed `CODE — note`, as every status log on this platform packs it.
   */
  last_reason: string | null;
  last_changed_at: string | null;
  last_changed_by: string | null;
}

/* ── Labels ────────────────────────────────────────────────────────────── */

const CHANNEL_LABEL: Record<string, string> = {
  IN_APP: 'In-app',
  EMAIL: 'Email',
  PUSH: 'Push',
  SMS: 'SMS',
};

/** "In-app + email + push" — the design's own form: first cap, the rest lower. */
function channelLabel(channels: string[] | null): string {
  if (!channels || channels.length === 0) return 'no channel recorded';
  const names = channels.map((c) => CHANNEL_LABEL[c] ?? c.toLowerCase().replace(/_/g, ' '));
  return names.map((n, i) => (i === 0 ? n : n.toLowerCase())).join(' + ');
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  LIVE: 'Live',
  SENT: 'Sent',
};

/** Live and Sent are the filled chip, Scheduled the outline, Draft the neutral. */
const STATUS_TONE: Record<string, 'accent' | 'outline' | 'neutral' | 'quiet'> = {
  LIVE: 'accent',
  SENT: 'accent',
  SCHEDULED: 'outline',
  DRAFT: 'neutral',
};

function statusLabel(status: string): string {
  return STATUS_LABEL[status] ?? status.toLowerCase();
}

/** The jsonb is untyped, so a non-string value is treated as no copy at all. */
function localeCopy(row: AnnouncementRow, locale: string): string | null {
  const value = row.copy?.[locale];
  return typeof value === 'string' && value.trim() ? value : null;
}

function announcementTitle(row: AnnouncementRow): string {
  return localeCopy(row, 'en-PH') ?? localeCopy(row, 'fil-PH') ?? 'No copy recorded';
}

/** True when the row carries a rule this platform can actually resolve. */
function hasResolvableRule(row: AnnouncementRow): boolean {
  const key = row.audience_rule?.rule;
  return typeof key === 'string' && Object.hasOwn(ANNOUNCEMENT_AUDIENCE_RULE_INFO, key);
}

function audienceLabel(row: AnnouncementRow): string {
  const key = row.audience_rule?.rule;
  if (typeof key === 'string' && Object.hasOwn(ANNOUNCEMENT_AUDIENCE_RULE_INFO, key)) {
    return ANNOUNCEMENT_AUDIENCE_RULE_INFO[key as AnnouncementAudienceRuleKey].label;
  }
  /* A row composed before the vocabulary existed. The sentence is shown because
     an operator wrote it and the next operator should read it — and it is
     labelled as words rather than as a rule, because nothing can send to it. */
  const described = row.audience_rule?.description;
  return described ? `${described} — written in words` : 'audience rule not recorded';
}

/**
 * MANDATORY versus PROMOTIONAL, and what the difference does.
 *
 * Not decoration and not only about an unsubscribe: a self-excluded player is
 * WITHHELD from a promotional send and INCLUDED in a mandatory one, which the
 * resolver enforces and `sql/340`'s trigger refuses to let past.
 */
function optOutLabel(kind: string): string {
  if (kind === 'MANDATORY') return 'Mandatory · ignores marketing opt-out';
  if (kind === 'PROMOTIONAL') return 'Promotional · respects marketing opt-out';
  return `${kind.toLowerCase()} · opt-out behaviour not defined for this kind`;
}

/**
 * "12 Sep 08:00". Asia/Manila, because platform.timezone is Asia/Manila.
 *
 * Assembled from parts rather than printed by the locale's own pattern: en-PH
 * orders the month first and adds a comma ("Sep 12, 08:00"), which is neither
 * what this screen documents nor what §18's calendar next door renders. Two
 * adjacent screens disagreeing about whether "09 Sep" means the 9th or
 * September is exactly the confusion the day-first order exists to prevent.
 */
const stamp = new Intl.DateTimeFormat('en-PH', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Asia/Manila',
});

/**
 * The human half of a packed `CODE — note` reason, with the code as a fallback.
 *
 * Every status log on this platform stores the pair in one column joined by
 * GAME_REASON_SEPARATOR, so one split serves all of them. The NOTE is what is
 * shown, because the code is a grouping key and the note is the sentence
 * somebody wrote for the operator who reads it next; a row whose note is missing
 * falls back to the code rather than rendering an empty cell.
 */
function reasonLabel(packed: string | null): string {
  if (!packed) return 'no reason recorded';
  const at = packed.indexOf(GAME_REASON_SEPARATOR);
  if (at === -1) return packed;
  return packed.slice(at + GAME_REASON_SEPARATOR.length).trim() || packed.slice(0, at);
}

function when(iso: string): string {
  const parts = stamp.formatToParts(new Date(iso));
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('day')} ${pick('month')} ${pick('hour')}:${pick('minute')}`;
}

/* ── Composing a send time ──────────────────────────────────────────────────
   The send moment is entered and read in Philippine time, because that is the
   clock every other timestamp on this screen is printed in. Asia/Manila has
   kept no daylight saving since 1978, so the offset is a fixed +08:00 and the
   conversion is exact — reading `datetime-local` in the BROWSER's zone would
   move a scheduled send by hours for an operator on a laptop set to UTC. */

const PHT_OFFSET = '+08:00';

const phtFields = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Manila',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** "2026-09-12T08:00" as typed in Manila → the instant, in ISO 8601. */
function phtToIso(local: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  const at = new Date(`${local}:00.000${PHT_OFFSET}`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** The reverse, for the composer's default. */
function isoToPht(iso: string): string {
  const parts = phtFields.formatToParts(new Date(iso));
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${pick('year')}-${pick('month')}-${pick('day')}T${pick('hour')}:${pick('minute')}`;
}

/** Why a queued send was called off. A closed set the contract validates. */
const CANCEL_REASONS = [
  { code: 'TIMING_CHANGED', label: 'Timing changed' },
  { code: 'COPY_INCORRECT', label: 'Copy is wrong' },
  { code: 'WRONG_AUDIENCE', label: 'Wrong audience' },
  { code: 'CAMPAIGN_WITHDRAWN', label: 'Campaign withdrawn' },
  { code: 'REGULATORY_HOLD', label: 'Regulatory hold' },
] as const;

/**
 * The design's cell reads "Timing · reach". Only the timing half is sourceable
 * — see the reach notice below — so the label is the half that is true.
 *
 * The TENSE is read off the clock, not off which column happens to be filled.
 * A LIVE row carries no sent_at (the seed only sets it on SENT) but does carry
 * a scheduled_for that has already passed, and "Sends 30 Aug 06:58" of an
 * instant three hours gone reads as a send still pending — beside a Status
 * column that says the announcement is in front of players right now. So a past
 * scheduled_for is reported as past: "Live since" when the row is LIVE, which
 * is the strongest thing the row supports, and the neutral "Was due" otherwise,
 * because a SCHEDULED row whose moment has passed is a send that did not run
 * and must not be dressed up as one that will.
 */
function timingLabel(row: AnnouncementRow): string {
  if (row.sent_at) return `Sent ${when(row.sent_at)}`;
  if (row.scheduled_for) {
    if (new Date(row.scheduled_for).getTime() > Date.now()) {
      return `Sends ${when(row.scheduled_for)}`;
    }
    return row.status === 'LIVE'
      ? `Live since ${when(row.scheduled_for)}`
      : `Was due ${when(row.scheduled_for)}`;
  }
  if (row.status === 'DRAFT') return 'Not scheduled';
  return `Created ${when(row.created_at)}`;
}

/** "English (PH) and Filipino are both required to publish." */
function hasBothLocales(row: AnnouncementRow): boolean {
  return row.has_english === 'true' && row.has_filipino === 'true';
}

/* ── Columns ───────────────────────────────────────────────────────────────
   A factory rather than a bare array only because the Action cell has to reach
   the screen's Schedule handler; everything else is a pure read of the row. */

function announcementColumns(
  onSchedule: (row: AnnouncementRow) => void,
  onCancel: (row: AnnouncementRow) => void,
): Column<AnnouncementRow>[] {
  return [
    {
      key: 'announcement',
      header: 'Announcement',
      render: (a) => (
        <>
          {announcementTitle(a)}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            <span className="tabular">{a.ref}</span> · {a.brand ?? 'all brands'}
          </div>
        </>
      ),
    },
    {
      key: 'audience',
      header: 'Audience',
      render: (a) => (
        <>
          {audienceLabel(a)}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {optOutLabel(a.kind)}
          </div>
        </>
      ),
    },
    {
      key: 'channel',
      header: 'Channel',
      secondary: true,
      render: (a) => <span className="text-muted">{channelLabel(a.channels)}</span>,
    },
    {
      key: 'copy',
      header: 'Copy',
      secondary: true,
      // Surfaced as a column because it is the one thing that decides whether a
      // Draft can leave Draft, and the endpoint already computes it per row.
      render: (a) =>
        hasBothLocales(a) ? (
          <Tag tone="neutral">EN + FIL</Tag>
        ) : a.has_english === 'true' ? (
          <Tag tone="outline">Filipino missing</Tag>
        ) : a.has_filipino === 'true' ? (
          <Tag tone="outline">English missing</Tag>
        ) : (
          <Tag tone="quiet">No copy</Tag>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <span className="row">
          {a.status === 'LIVE' ? <StatusDot tone="ok" live label="Showing to players now" /> : null}
          <Tag tone={STATUS_TONE[a.status] ?? 'quiet'}>{statusLabel(a.status)}</Tag>
        </span>
      ),
    },
    {
      key: 'timing',
      header: 'Timing',
      render: (a) => <span className="text-muted">{timingLabel(a)}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      /*
       * "The Schedule button renders only when the row status is Draft" — and
       * a Scheduled row gets the one action that is its own opposite. A Live or
       * Sent row gets neither: there is no forward action on an announcement
       * players already have, and an empty cell says so more honestly than a
       * disabled control the operator will keep trying.
       */
      render: (a) =>
        a.status === 'DRAFT' ? (
          <Button
            small
            variant="primary"
            onClick={(e) => {
              // The row itself opens the drawer; the button must not do both.
              e.stopPropagation();
              onSchedule(a);
            }}
          >
            Schedule
          </Button>
        ) : a.status === 'SCHEDULED' ? (
          <Button
            small
            onClick={(e) => {
              e.stopPropagation();
              onCancel(a);
            }}
          >
            Cancel send
          </Button>
        ) : null,
    },
  ];
}

/**
 * The Delivery report's columns.
 *
 * Announcement, recipients, and then the outbox's own verdict split five ways.
 * NOT "reach, opened, tapped": two of those three have no source and the third
 * would be a rename of `delivered` that implies measurement nobody performed.
 */
const deliveryColumns: Column<DeliveryRow>[] = [
  {
    key: 'announcement',
    header: 'Announcement',
    render: (d) => (
      <>
        <span className="tabular">{d.ref}</span>
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {d.kind === 'PROMOTIONAL' ? 'Promotional' : 'Mandatory'} · {d.brand ?? 'all brands'}
        </div>
      </>
    ),
  },
  {
    key: 'recipients',
    header: 'Written to',
    numeric: true,
    render: (d) => <span className="tabular">{formatCount(d.recipients)}</span>,
  },
  {
    key: 'delivered',
    header: 'Delivered',
    numeric: true,
    render: (d) => <span className="tabular">{formatCount(d.delivered)}</span>,
  },
  {
    key: 'queued',
    header: 'Queued',
    numeric: true,
    secondary: true,
    render: (d) => <span className="tabular">{formatCount(d.in_flight + d.retrying)}</span>,
  },
  {
    key: 'suppressed',
    header: 'Suppressed',
    numeric: true,
    secondary: true,
    render: (d) => <span className="tabular">{formatCount(d.suppressed)}</span>,
  },
  {
    key: 'dead',
    header: 'Dead',
    numeric: true,
    secondary: true,
    // DEAD is terminal and is meant to be looked at: somebody was not told
    // something this platform decided to tell them.
    render: (d) =>
      d.dead > 0 ? (
        <Tag tone="outline">{formatCount(d.dead)}</Tag>
      ) : (
        <span className="tabular">0</span>
      ),
  },
  {
    key: 'sent_at',
    header: 'Sent',
    render: (d) => (
      <span className="text-muted">
        {d.sent_at ? when(d.sent_at) : when(d.first_recorded_at)}
      </span>
    ),
  },
];

const notificationColumns: Column<NotificationRow>[] = [
  {
    key: 'setting',
    header: 'Setting',
    render: (n) => (
      <>
        {n.label}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {n.description}
        </div>
      </>
    ),
  },
  {
    key: 'channels',
    header: 'Channels',
    secondary: true,
    render: (n) => <span className="text-muted">{channelLabel(n.channels)}</span>,
  },
  {
    key: 'basis',
    header: 'Basis',
    secondary: true,
    render: (n) =>
      n.mandatory ? (
        <Tag tone="outline">Mandatory by licence</Tag>
      ) : (
        <span className="text-subtle">Operator choice</span>
      ),
  },
  {
    key: 'state',
    header: 'State',
    // Colour never carries this on its own: the dot is paired with the word. Where the
    // switch stands AND what really reaches a player, which the switch does not decide —
    // nothing reads it yet (notification-state.ts).
    render: (n) => {
      const state = notificationState({ key: n.key, enabled: n.enabled, mandatory: n.mandatory });
      return (
        <span className="row">
          <StatusDot tone={state.tone} label={state.label} />
          <span>{state.words}</span>
        </span>
      );
    },
  },
  {
    key: 'last',
    header: 'Last changed',
    secondary: true,
    render: (n) =>
      n.last_changed_at === null ? (
        /* Never switched is not the same fact as switched back to where it
           started, so it is said rather than left blank. */
        <span className="text-subtle">never changed</span>
      ) : (
        <>
          <div>{reasonLabel(n.last_reason)}</div>
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            {when(n.last_changed_at)} · {n.last_changed_by ?? 'an admin since removed'}
          </div>
        </>
      ),
  },
];

/**
 * The columns with the switch on the end.
 *
 * A function rather than a constant because the control needs the permission
 * and the handler, and folding those into a module-level array is how a button
 * ends up gated on nothing.
 */
function notificationColumnsWith(options: {
  canSwitch: boolean;
  onSwitch: (n: NotificationRow) => void;
}): Column<NotificationRow>[] {
  return [
    ...notificationColumns,
    {
      key: 'action',
      header: 'Switch',
      render: (n) =>
        n.mandatory ? (
          /* NOT a disabled switch. The licence forbids this one being off, so
             the row states the rule instead of drawing a control that would be
             refused by the route and by the database behind it. */
          <span className="text-subtle">mandatory — stays on</span>
        ) : !options.canSwitch ? (
          <span className="text-subtle">needs Manage roles &amp; users</span>
        ) : (
          <Button small onClick={() => options.onSwitch(n)}>
            {n.enabled ? 'Switch off' : 'Switch on'}
          </Button>
        ),
    },
  ];
}

/* The composer's option lists moved to ComposeAnnouncementDialog.tsx, which is
   where they are now actually used. The channel list became the four codes the
   column and @dayon/contracts hold (IN_APP · EMAIL · PUSH · SMS) rather than
   four prose combinations of them, because the form now WRITES the array; and
   the timing list is gone, because composing always creates a DRAFT and
   scheduling is a separate, separately-permissioned step — an option reading
   "Send immediately" on a form that cannot send is exactly the kind of claim
   this console does not make. */

const STATUS_CHIPS = [
  { key: 'ALL', label: 'All' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'LIVE', label: 'Live' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SENT', label: 'Sent' },
] as const;

type StatusChip = (typeof STATUS_CHIPS)[number]['key'];

/**
 * Notifications & announcements — §19.
 *
 * Player-facing messages, and the rules behind the automatic ones. A draft can
 * be composed, its copy rewritten, a send queued for a moment, a queued send
 * called off — and now SENT, to a counted audience, through the same outbox the
 * invites go through. The delivery report reads that outbox.
 *
 * The automatic notifications below can now be switched on and off, each move
 * carrying a reason code and a note into `platform.notification_setting_change`
 * and the audit trail in one transaction. One row has no switch and will never
 * have one: "Account restriction applied" is mandatory by licence, and
 * `710_notification_setting_writes.sql` holds that in a CHECK so no writer at
 * all can silence it.
 */
export function Announcements() {
  const { brandId, brands, session, allowed } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canManageAccess = allowed('access.manage');
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [rows, setRows] = useState<AnnouncementRow[]>([]);
  const [settings, setSettings] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);
  /*
   * Did the last read FAIL, or did it succeed and return nothing?
   *
   * The error dialog alone could not carry that: once it was dismissed the
   * table fell through to the empty state written for a genuinely empty scope
   * ("This is a real zero"), the header read "0 announcements" and every tile
   * read 0 — a confident zero produced by a request that never came back. The
   * two panels here are backed by two independent GETs, so each keeps its own
   * flag: /api/platform/notifications can be healthy while the announcement
   * list is down, and one shared flag would condemn the panel that loaded.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [settingsFailed, setSettingsFailed] = useState(false);
  const [status, setStatus] = useState<StatusChip>('ALL');
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'compose' | 'delivery' | null>(null);

  /* ── Arriving from the players roster ─────────────────────────────────────
   *
   * `Players` hands a selection over as `?audience=NAMED_PLAYERS&players=a,b,c`
   * rather than carrying it in memory, because the two screens are separate
   * routes and the link has to survive a new tab. This is the half that reads
   * it: without it the link navigated here, the page rendered its ordinary
   * self, and the selection was dropped without a word — a control that looked
   * like it had worked.
   *
   * The ids are not validated here. The composer's own count resolves them
   * against the brand scope and reports how many matched, which is the number
   * that decides anything; an id that names nobody simply does not appear in it.
   */
  const [params, setParams] = useSearchParams();
  const handedOverPlayers = useMemo(() => {
    if (params.get('audience') !== 'NAMED_PLAYERS') return null;
    const ids = (params.get('players') ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);
    return ids.length > 0 ? ids : null;
  }, [params]);

  /* Opened by the address, once. A `replace` rather than a push so Back returns
     to the roster the operator came from, and so a reload after closing the
     composer does not re-arm a selection they have finished with. */
  useEffect(() => {
    if (handedOverPlayers) setDialog('compose');
  }, [handedOverPlayers]);

  const closeCompose = useCallback(() => {
    setDialog(null);
    if (params.has('audience') || params.has('players')) {
      const next = new URLSearchParams(params);
      next.delete('audience');
      next.delete('players');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  /* ── Writing ──────────────────────────────────────────────────────────────
     Bumped after every accepted write so the list re-reads. A row patched in
     place would leave the four tiles and the status chips counting the board as
     it was — including "Blocked on a locale", which is the number that decides
     whether anything can go out at all. */
  const [writeCount, setWriteCount] = useState(0);
  /**
   * Its own counter, not `writeCount`.
   *
   * The announcement list re-reads on `writeCount`, and this table is a separate
   * request against a table no announcement write touches — so sharing the
   * counter would refetch the settings after every send, and the settings effect
   * deliberately does not depend on the brand for the same reason. Switching a
   * notification conversely must NOT re-read the announcement list.
   */
  const [settingWrites, setSettingWrites] = useState(0);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState<AnnouncementRow | null>(null);
  const [sendAt, setSendAt] = useState('');
  const [sendAtError, setSendAtError] = useState<string | undefined>(undefined);
  const [cancellingSend, setCancellingSend] = useState<AnnouncementRow | null>(null);
  const [cancelReason, setCancelReason] = useState<string>(CANCEL_REASONS[0].code);
  const [cancelNote, setCancelNote] = useState('');

  /* ── Sending ──────────────────────────────────────────────────────────────
     The audience is resolved when the confirmation opens and again by the route
     inside its own transaction. `audience` here is what the operator is SHOWN,
     and it is what gets passed back as `expectedRecipients` — so if the set has
     moved in between, the route refuses rather than sending a number nobody
     approved. */
  const [sending, setSending] = useState<AnnouncementRow | null>(null);
  const [sendAudience, setSendAudience] = useState<AudienceCount | null>(null);
  const [sendBlockers, setSendBlockers] = useState<{ code: string; detail: string }[]>([]);
  const [countingAudience, setCountingAudience] = useState(false);
  const [countFailed, setCountFailed] = useState(false);

  /** Per-announcement delivery, straight off platform.mail_message. */
  const [deliveries, setDeliveries] = useState<DeliveryRow[]>([]);
  const [deliveriesFailed, setDeliveriesFailed] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canManageAccess) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<{ items: AnnouncementRow[] }>(
        `/api/platform/announcements${query ? `?${query}` : ''}`,
      )
      .then((d) => {
        if (cancelled) return;
        setRows(d.items);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared, not left as it was: a list from the previous brand scope
        // sitting under a failure notice would read as this scope's answer.
        setRows([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load announcements',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canManageAccess, brandId, showError, writeCount]);

  // Separate effect, and no brandId: platform.notification_setting is a single
  // platform-wide table with no brand column, so re-fetching it on every brand
  // switch would be a request that cannot change its own answer.
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canManageAccess) {
      return;
    }
    let cancelled = false;
    api
      .get<{ items: NotificationRow[] }>('/api/platform/notifications')
      .then((d) => {
        if (cancelled) return;
        setSettings(d.items);
        setSettingsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSettings([]);
        setSettingsFailed(true);
        showError({
          title: 'Could not load automatic notifications',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoadingSettings(false));

    return () => {
      cancelled = true;
    };
  }, [canManageAccess, showError, settingWrites]);

  /* Re-read after every write, because a send changes it. Its own request
     rather than a field on the list: the list is one row per announcement and
     this is an aggregate over two other tables, and a failure of one must not
     empty the other — the same reasoning the notification panel already uses. */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canManageAccess) {
      setLoadingDeliveries(false);
      return;
    }
    let cancelled = false;
    setLoadingDeliveries(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const query = params.toString();

    api
      .get<{ items: DeliveryRow[] }>(
        `/api/platform/announcements/deliveries${query ? `?${query}` : ''}`,
      )
      .then((d) => {
        if (cancelled) return;
        setDeliveries(d.items);
        setDeliveriesFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        /* No error dialog here. This request backs a panel the operator has not
           necessarily opened, and a modal for a background read they did not
           ask for would interrupt the work they did ask for. The panel says the
           read failed, which is the honest thing and is not a zero. */
        setDeliveries([]);
        setDeliveriesFailed(true);
      })
      .finally(() => !cancelled && setLoadingDeliveries(false));

    return () => {
      cancelled = true;
    };
  }, [canManageAccess, brandId, writeCount]);

  const visible = useMemo(
    () => (status === 'ALL' ? rows : rows.filter((r) => r.status === status)),
    [rows, status],
  );

  /** The delivery record for one announcement, or null when it was never sent. */
  const deliveryFor = useCallback(
    (ref: string) => deliveries.find((d) => d.ref === ref) ?? null,
    [deliveries],
  );

  const counts = useMemo(() => {
    const by = (s: string) => rows.filter((r) => r.status === s).length;
    return {
      draft: by('DRAFT'),
      scheduled: by('SCHEDULED'),
      live: by('LIVE'),
      sent: by('SENT'),
      // Drafts that cannot be published as they stand — the rule made countable.
      blocked: rows.filter((r) => r.status !== 'SENT' && !hasBothLocales(r)).length,
    };
  }, [rows]);

  const selected = useMemo(
    () => rows.find((r) => r.ref === selectedRef) ?? null,
    [rows, selectedRef],
  );

  const brandLabel = brandId
    ? (brands.find((b) => b.id === brandId)?.name ?? 'selected brand')
    : 'all brands';

  /**
   * "Schedule is permission-gated on announce.publish."
   *
   * That key DOES NOT EXIST: @dayon/rbac ships eleven permissions and
   * announce.publish is not one of them, so `allowed('announce.publish')` would
   * not compile. Rather than borrow a VIEW permission to gate a write — the
   * mistake a test in this app exists to catch — both routes require
   * access.manage, which is a write capability, mandatory two-factor, and the
   * same one the read behind this screen already enforces. This gate is
   * therefore the route's gate, not a looser local guess. The missing
   * capability is reported rather than papered over: a role that should be able
   * to publish an announcement without also being able to edit roles has
   * nowhere to sit in the current matrix.
   */
  const canPublish = allowed('access.manage');

  /**
   * COMPOSING is `platform.manage`, and that is a different key from the one
   * above.
   *
   * The composer and the copy rewrite are new routes and were gated on the
   * capability that fits them — `platform.manage`, "configure brands,
   * parameters & announcements". The schedule, the cancel and the list beside
   * them are older and are gated on `access.manage`, for the reason the note
   * above records: the design's `announce.publish` does not exist in the
   * matrix.
   *
   * That leaves a seam, and it is stated rather than hidden: a role holding
   * `platform.manage` but not `access.manage` could compose a draft and could
   * not schedule it — indeed could not open this screen. It is unreachable with
   * the shipped matrix, because Super Admin is the only role holding
   * `platform.manage` and it holds `access.manage` too, and it is asserted in
   * `services/api/src/__tests__/brand-operations.integration.test.ts`. Closing
   * it properly means re-gating the three older routes, which is a change to a
   * live capability and to the e2e assertions that cover it.
   */
  const mayCompose = allowed('platform.manage');

  /** The draft whose copy is being rewritten, or null. */
  const [rewriting, setRewriting] = useState<DraftCopy | null>(null);

  /**
   * The automatic notification being switched, or null.
   *
   * `canPublish` is the gate — `access.manage`, the same capability the READ
   * behind this panel already requires and the same one the route enforces.
   * A role that cannot hold it never reaches this screen at all, so the column
   * states the requirement rather than rendering a control that cannot work.
   */
  const [switching, setSwitching] = useState<NotificationSetting | null>(null);

  const openRewriter = useCallback(
    (row: AnnouncementRow) => {
      if (!mayCompose) {
        showError({
          title: `The copy on ${row.ref} cannot be rewritten by this role`,
          message:
            `Writing an announcement's copy needs platform.manage — your role is ${session?.role ?? 'unknown'}. ` +
            'It is the same capability that composes one, and it is separate from access.manage, ' +
            'which is what schedules a send.',
        });
        return;
      }
      setRewriting({
        id: row.id,
        ref: row.ref,
        status: row.status,
        english: localeCopy(row, 'en-PH'),
        filipino: localeCopy(row, 'fil-PH'),
      });
    },
    [mayCompose, session, showError],
  );

  const openScheduler = useCallback(
    (row: AnnouncementRow) => {
      // Permission first: an operator who may not publish should be told that,
      // not walked into a form whose submit they cannot use.
      if (!canPublish) {
        showError({
          title: `${row.ref} cannot be scheduled by this role`,
          message:
            /* The design's announce.publish key does not exist; the schedule route enforces
               access.manage. */
            `Scheduling an announcement needs access.manage — your role is ${session?.role ?? 'unknown'}. ` +
            'Ask somebody whose role holds access.manage to schedule it.',
        });
        return;
      }
      /*
       * Then the publish precondition, which is a data rule rather than a role
       * one. The route refuses this too — it is the only thing standing between
       * a Filipino-speaking player and an empty notification — but there is no
       * point opening a form whose submit is already known to be refused.
       */
      if (!hasBothLocales(row)) {
        showError({
          title: `${row.ref} is missing a locale`,
          message:
            'English (PH) and Filipino are both required before an announcement can be scheduled. ' +
            `${row.has_english === 'true' ? 'The Filipino' : 'The English (PH)'} copy has not been written, so that share of the audience would receive nothing.`,
        });
        return;
      }
      // Tomorrow, 09:00 Manila — inside business hours and comfortably ahead,
      // so the form does not open onto a moment the route will refuse.
      const base = new Date(Date.now() + 24 * 60 * 60 * 1000);
      setSendAt(`${isoToPht(base.toISOString()).slice(0, 10)}T09:00`);
      setSendAtError(undefined);
      setScheduling(row);
    },
    [canPublish, session, showError],
  );

  const submitSchedule = useCallback(async () => {
    if (!scheduling) return;
    const iso = phtToIso(sendAt);
    if (!iso) {
      setSendAtError('Give the date and time the send should run.');
      return;
    }
    if (new Date(iso).getTime() <= Date.now()) {
      setSendAtError('Pick a time still ahead — a send cannot be queued for a moment that passed.');
      return;
    }
    setSendAtError(undefined);

    setSaving(true);
    try {
      await api.post(`/api/platform/announcements/${scheduling.id}/schedule`, {
        scheduledFor: iso,
      });
      toast(`${scheduling.ref} queued for ${when(iso)} PHT.`);
      setScheduling(null);
      setSelectedRef(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields?.['scheduledFor']) {
        setSendAtError(error.fields['scheduledFor']);
      }
      showError({
        title: `${scheduling.ref} was not scheduled`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was queued. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [scheduling, sendAt, showError, toast]);

  const submitCancel = useCallback(async () => {
    if (!cancellingSend) return;
    setSaving(true);
    try {
      await api.post(`/api/platform/announcements/${cancellingSend.id}/cancel`, {
        reasonCode: cancelReason,
        note: cancelNote.trim(),
      });
      toast(`${cancellingSend.ref} is back to draft — nothing will be sent.`);
      setCancellingSend(null);
      setCancelNote('');
      setSelectedRef(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: `Could not cancel the send on ${cancellingSend.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The announcement is unchanged. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [cancellingSend, cancelReason, cancelNote, showError, toast]);

  const openCanceller = useCallback((row: AnnouncementRow) => {
    setCancelReason(CANCEL_REASONS[0].code);
    setCancelNote('');
    setCancellingSend(row);
  }, []);

  /**
   * Open the send confirmation, and count the audience while it opens.
   *
   * The count is fetched HERE rather than on the drawer, so the number an
   * operator confirms against is one they asked for a moment ago rather than
   * one that has been sitting on screen since the page loaded. It is passed
   * back as `expectedRecipients` and the route refuses a disagreement.
   */
  const openSender = useCallback(
    (row: AnnouncementRow) => {
      if (!canPublish) {
        showError({
          title: `${row.ref} cannot be sent by this role`,
          message:
            `Sending an announcement needs access.manage — your role is ${session?.role ?? 'unknown'}. ` +
            'It is the same capability that schedules one, and it is the widest action on this ' +
            'screen: it writes to every player the audience rule resolves to.',
        });
        return;
      }
      setSending(row);
      setSendAudience(null);
      setSendBlockers([]);
      setCountFailed(false);
      setCountingAudience(true);
      api
        .get<{ audience: AudienceCount; blockers: { code: string; detail: string }[] }>(
          `/api/platform/announcements/${row.id}/audience`,
        )
        .then((d) => {
          setSendAudience(d.audience);
          setSendBlockers(d.blockers);
        })
        .catch((error: unknown) => {
          /* Cleared rather than left stale, and never replaced by a zero. */
          setSendAudience(null);
          setCountFailed(true);
          showError({
            title: `The audience for ${row.ref} could not be counted`,
            message:
              error instanceof ApiRequestError
                ? error.message
                : 'Nothing was counted, and nothing was sent.',
            ...(error instanceof ApiRequestError ? { code: error.code } : {}),
          });
        })
        .finally(() => setCountingAudience(false));
    },
    [canPublish, session, showError],
  );

  const submitSend = useCallback(async () => {
    if (!sending || !sendAudience) return;
    setSaving(true);
    try {
      const result = await api.post<{
        recipients: number;
        queued: number;
        suppressedAtRecord: number;
      }>(`/api/platform/announcements/${sending.id}/send`, {
        expectedRecipients: sendAudience.recipients,
      });
      toast(
        `${sending.ref} sent to ${formatCount(result.recipients)} ${result.recipients === 1 ? 'person' : 'people'}` +
          (result.suppressedAtRecord > 0
            ? ` · ${formatCount(result.suppressedAtRecord)} suppressed`
            : ''),
      );
      setSending(null);
      setSelectedRef(null);
      setWriteCount((n) => n + 1);
    } catch (error: unknown) {
      showError({
        title: `${sending.ref} was not sent`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written to anybody. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  }, [sending, sendAudience, showError, toast]);

  if (!canManageAccess) {
    return (
      <EmptyState icon="lock-key" title="Notifications & announcements">
        This module needs the access.manage capability — the same one the platform asks for
        before it shows announcements or notification settings. Your role does not hold it.
      </EmptyState>
    );
  }

  // A chip badge counts rows that were read. With the read failed there are no
  // rows to count, and a row of zeroes would say every status is empty — so the
  // badges are omitted rather than set to nothing.
  const chips = loadFailed
    ? STATUS_CHIPS.map((c) => ({ key: c.key, label: c.label }))
    : STATUS_CHIPS.map((c) => ({
        key: c.key,
        label: c.label,
        count: c.key === 'ALL' ? rows.length : rows.filter((r) => r.status === c.key).length,
      }));

  return (
    <>
      <ScreenHeader
        /*
          THE HEADING KEEPS BOTH WORDS, THOUGH THE SIDEBAR SAYS ONLY ONE.

          The sidebar calls this module "Announcements", and the h3 this replaces
          said "Notifications & announcements" because the screen is two jobs in
          one place: the table below is the announcements somebody writes,
          schedules and sends, and a second panel further down is the automatic
          messages the platform sends on its own, which are switched on and off
          here and nowhere else. Narrowing the heading to match the sidebar would
          tell an operator who arrived looking for that switch that they were on
          the wrong screen, and there is no other screen for them to go to — so
          the broader name stays, and the purpose below says both halves out loud.
        */
        title="Notifications & announcements"
        purpose="For whoever has to tell players something: write the message, choose when it goes out, send it, and switch the automatic ones on or off."
        /*
          The caption kept exactly as it was, all three states and the scope.

          It is tri-state and the three states are NOT interchangeable:
          "Loading…" means the answer has not arrived yet, "the announcement list
          could not be read" means the request failed and so nothing was counted
          at all, and the count means the list genuinely answered. Collapsing the
          middle state into a zero would turn a failed read into the claim that
          nothing has ever been announced, which is the most expensive thing this
          screen could say to somebody deciding whether a notice went out — and
          it is the same distinction the three tiles and the empty state below
          both go out of their way to keep.

          The brand suffix stays a SEPARATE expression appended to whichever
          state won, because it is true of all three: a read that failed, failed
          within a scope, and a reader who does not know whether they are looking
          at one brand or at all of them cannot judge the number either way.
        */
        meta={
          <>
            {loading
              ? 'Loading…'
              : loadFailed
                ? 'the announcement list could not be read'
                : `${formatCount(rows.length)} announcement${rows.length === 1 ? '' : 's'} · ${formatCount(counts.draft)} draft`}{' '}
            · {brandLabel}
          </>
        }
        actions={
          <>
            <Button onClick={() => setDialog('delivery')}>
              <Icon name="chart-bar" />
              Delivery reports
            </Button>
            {/* Disabled with its true reason rather than hidden: this screen is
                reached with access.manage, and composing needs platform.manage —
                an operator who can schedule a draft but cannot write one has to
                be told which capability is missing, not shown a screen with no
                way to add anything to it. */}
            <Button
              variant="primary"
              disabled={!mayCompose}
              aria-label={
                mayCompose
                  ? undefined
                  : 'Compose — you do not hold platform.manage, which composing an announcement requires'
              }
              onClick={() => setDialog('compose')}
            >
              <Icon name="plus" />
              Compose
            </Button>
          </>
        }
      />

      {/*
       * The column deciding who is reachable is `kind`, and the sentence below
       * no longer says so. An operator has no column in front of them: they
       * have the Kind row in a drawer and Mandatory or Promotional printed
       * under every title, and those carry the same fact in words they already
       * use. The value span went with the name — the markup is there to mark a
       * literal the reader is looking at, and a column name is not one.
       */}
      <div className="notice notice-quiet">
        <Icon name="broadcast" />
        <div>
          An audience rule is <strong>resolved when the send runs</strong>, so a count is a
          measurement of one moment rather than a promise about a later one — the composer counts
          it while you write, the Send confirmation counts it again, and a send whose size has
          moved in between is refused rather than absorbed. The list below shows no count on any
          row: the list is read once for every announcement, not counted audience by audience, and
          a number here would be as stale as the page. A notice&rsquo;s <strong>Kind</strong> decides who is reachable
          at all: a <strong>mandatory</strong> notice — restrictions, licence, maintenance — ignores
          marketing opt-out and still reaches a self-excluded player, and a{' '}
          <strong>promotional</strong> one never does and never reaches one. Sending needs both
          locales, English (PH) and Filipino.
        </div>
      </div>

      <div className="stat-grid">
        {/* A tile counted off rows that never arrived would read 0, and 0 is a
            claim. While the read is failed these three say so instead. */}
        <Stat
          label="Announcements in scope"
          value={loading || loadFailed ? '—' : formatCount(rows.length)}
          delta={
            loadFailed
              ? 'not counted — the announcement read failed'
              : `${formatCount(counts.draft)} draft · ${formatCount(counts.scheduled)} scheduled`
          }
        />
        <Stat
          label="Live now"
          value={loading || loadFailed ? '—' : formatCount(counts.live)}
          delta={
            loadFailed
              ? 'not counted — the announcement read failed'
              : `${formatCount(counts.sent)} sent previously`
          }
        />
        <Stat
          label="Blocked on a locale"
          value={loading || loadFailed ? '—' : formatCount(counts.blocked)}
          delta={
            loadFailed
              ? 'not counted — the announcement read failed'
              : 'both locales required to publish'
          }
          deltaUp={!loadFailed && counts.blocked > 0}
        />
        <Stat
          label="People written to"
          /*
           * The design asked for "Estimated reach · 43,120 est.", and this tile
           * read "Not stored" for as long as that was the only honest answer:
           * `platform.announcement` carries no reach column, nothing evaluated
           * the audience rule, and a plausible number here is the worst possible
           * answer because somebody would size a campaign against it.
           *
           * What it counts now is not an estimate at all. It is the number of
           * `platform.announcement_delivery` rows across the announcements in
           * this scope — people who were actually written to, summed from the
           * record of the sends that happened. There is still no ESTIMATE of a
           * future send here, because that number belongs to one audience rule
           * and this tile spans every announcement on the screen; the estimate
           * for a particular send is counted in the composer and again in the
           * send confirmation.
           */
          value={
            loadingDeliveries || deliveriesFailed
              ? '—'
              : formatCount(deliveries.reduce((n, d) => n + d.recipients, 0))
          }
          delta={
            deliveriesFailed
              ? 'not counted — the delivery record could not be read'
              : /* Counted across the announcements with a delivery record, which is not
                   every one marked sent: one sent before the record existed has none, and
                   the tile read "across 0 sent announcements" beside two marked sent
                   (module walk, 30 Oct 2026). */
                `across ${formatCount(deliveries.length)} announcement${deliveries.length === 1 ? '' : 's'} with a delivery record · no open or tap figure exists`
          }
        />
      </div>

      <div className="row">
        <span className="panel-title">Status</span>
        <Chips
          options={chips}
          active={status}
          onChange={setStatus}
          label="Filter announcements by status"
        />
      </div>

      <Panel
        title={
          loadFailed
            ? 'Announcements · not read'
            : `Announcements · ${formatCount(visible.length)} in view`
        }
      >
        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '260px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Announcements with audience, channel, status and timing. Draft rows can be scheduled."
            columns={announcementColumns(openScheduler, openCanceller)}
            rows={visible}
            rowKey={(a) => a.id}
            onRowClick={(a) => setSelectedRef(a.ref)}
            empty={
              <EmptyState
                icon={loadFailed ? 'warning-circle' : 'broadcast'}
                title={
                  loadFailed
                    ? 'The announcement list could not be read'
                    : status === 'ALL'
                      ? 'Nothing has been announced'
                      : `No ${statusLabel(status).toLowerCase()} announcements`
                }
              >
                {/* The failed read is answered FIRST, and never with a zero:
                    what is on screen is the absence of an answer, not an
                    answer of none. */}
                {loadFailed
                  ? `The request for ${brandLabel} did not come back, so this table is empty because nothing was read — not because nothing has been announced. The error dialog carries what the platform said; try again shortly.`
                  : status === 'ALL'
                    ? `Nothing has been composed for ${brandLabel}. This is a real zero — the list is read live and unpaginated.`
                    : `No announcement in ${brandLabel} is at ${statusLabel(status).toLowerCase()}. The chips filter the rows already loaded.`}
              </EmptyState>
            }
          />
        )}

        {/*
         * What that press stamps is `sent_at`. The paragraph says "the sent
         * time" because that is the thing an operator can actually look at —
         * the Sent column on the delivery table, and the Timing line in a
         * row's drawer. Naming the column told them nothing they could check,
         * and dressing it as a value in a `.tabular` span made a schema
         * detail look like something on their screen.
         */}
        <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
          <Icon name="info" />
          <div>
            <strong>Schedule</strong> queues a draft for a moment, <strong>Cancel send</strong>{' '}
            takes a queued one back to draft keeping its copy, and <strong>Send now</strong> — in
            the row&rsquo;s drawer — resolves the audience and writes to every person in it. Each
            of the three is saved together with its audit entry, all or nothing, and a send saves
            its outgoing messages and its delivery records in that same step.
            <br />
            <strong>Nothing fires a queued send by itself.</strong> Nothing sends on a timer, so a
            Scheduled row passes its moment and stays Scheduled until somebody presses{' '}
            <strong>Send now</strong>. The sent time is recorded by that press and by nothing
            else. The title is read out of the{' '}
            announcement&rsquo;s body, because the announcement record has no title field of its
            own — so this table cannot be sorted or searched by it, and the chips filter only what is
            already loaded. Every announcement in scope arrives in one page, newest first.
          </div>
        </div>
      </Panel>

      <Panel
        title="Automatic player notifications"
        actions={
          <span className="text-subtle">
            {settings.filter((n) => !n.enabled).length === 0
              ? 'all switched on'
              : `${settings.filter((n) => !n.enabled).length} switched off`}
          </span>
        }
      >
        {loadingSettings && settings.length === 0 ? (
          <div className="skeleton" style={{ height: '180px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="System-generated player notifications, with the channels each lists, where its switch stands and what actually reaches a player"
            columns={notificationColumnsWith({
              canSwitch: canPublish,
              onSwitch: (n) =>
                setSwitching({
                  key: n.key,
                  label: n.label,
                  description: n.description,
                  mandatory: n.mandatory,
                  enabled: n.enabled,
                }),
            })}
            rows={settings}
            rowKey={(n) => n.key}
            empty={
              settingsFailed ? (
                <EmptyState
                  icon="warning-circle"
                  title="The notification settings could not be read"
                >
                  The notification settings could not be fetched, so this
                  table is empty because nothing was read. It changes nothing a player receives:
                  nothing reads these settings yet. The announcement panel above is a separate
                  request, so this failure says nothing about it either way.
                </EmptyState>
              ) : (
                <EmptyState icon="bell-slash" title="No automatic notifications configured">
                  The list of automatic notifications came back empty, so there is nothing to
                  switch. Nothing reads these settings yet, so what reaches a player is unchanged by
                  it: the KYC outcome is still emailed, and identity and withdrawal decisions still
                  show in the player app.
                </EmptyState>
              )
            }
          />
        )}

        <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
          <Icon name="info" />
          <div>
            <strong>Nothing reads these switches yet.</strong> What reaches a player does not depend
            on them: the KYC outcome is emailed and shown in the player app, and a withdrawal
            decision is shown in the player app, whichever way their switches stand; nothing sends a
            promotion-eligibility notice, a restriction notice or the weekly digest, on or off. The
            State column says which, row by row. Every switch carries a reason and a note, recorded
            against the setting and in the platform audit trail in one transaction, which is what
            the <strong>Last changed</strong> column reads.
            <br />
            <strong>Account restriction applied</strong> has no switch, because the licence requires
            a player to be told when a restriction lands on their account. The platform itself
            refuses to store it switched off, so nothing — not this screen, and not a change made
            behind it — can switch it off. Nothing
            sends that notice yet, which is why its State reads as needing a look.
          </div>
        </div>
      </Panel>

      {/* ── Row detail ────────────────────────────────────────────────────
          The prototype toasts "Opening AN-3301 — copy per locale, audience
          rules and delivery report". Two of those three are real fields on the
          response, so they are shown rather than announced. */}
      <Drawer
        open={selected !== null}
        onClose={() => setSelectedRef(null)}
        title={selected ? announcementTitle(selected) : ''}
        subtitle={
          selected
            ? `${selected.ref} · ${statusLabel(selected.status)} · ${selected.brand ?? 'all brands'}`
            : undefined
        }
        actions={
          selected?.status === 'DRAFT' ? (
            <>
              <Button
                disabled={!mayCompose}
                aria-label={
                  mayCompose
                    ? undefined
                    : 'Rewrite copy — you do not hold platform.manage, which writing copy requires'
                }
                onClick={() => openRewriter(selected)}
              >
                Rewrite copy
              </Button>
              <Button onClick={() => openScheduler(selected)}>Schedule</Button>
              {/* Disabled with its TRUE reason rather than hidden. A row whose
                  audience was written in words before the vocabulary existed
                  cannot be sent by anything, and an operator has to be told
                  that rather than left pressing a button that fails. */}
              <Button
                variant="primary"
                disabled={!hasResolvableRule(selected)}
                aria-label={
                  hasResolvableRule(selected)
                    ? undefined
                    : 'Send — this announcement describes its audience in words rather than as a rule, so nothing can resolve it into people'
                }
                onClick={() => openSender(selected)}
              >
                Send now
              </Button>
            </>
          ) : selected?.status === 'SCHEDULED' ? (
            <>
              <Button onClick={() => openCanceller(selected)}>Cancel send</Button>
              <Button
                variant="primary"
                disabled={!hasResolvableRule(selected)}
                aria-label={
                  hasResolvableRule(selected)
                    ? undefined
                    : 'Send — this announcement describes its audience in words rather than as a rule, so nothing can resolve it into people'
                }
                onClick={() => openSender(selected)}
              >
                Send now
              </Button>
            </>
          ) : undefined
        }
      >
        {selected ? (
          <div className="stack">
            <dl className="stack" style={{ margin: 0 }}>
              <div className="def-row">
                <dt>Reference</dt>
                <dd className="tabular">{selected.ref}</dd>
              </div>
              <div className="def-row">
                <dt>Kind</dt>
                <dd>{optOutLabel(selected.kind)}</dd>
              </div>
              <div className="def-row">
                <dt>Audience rule</dt>
                <dd>{audienceLabel(selected)}</dd>
              </div>
              <div className="def-row">
                <dt>Channels</dt>
                <dd>{channelLabel(selected.channels)}</dd>
              </div>
              <div className="def-row">
                <dt>Timing</dt>
                <dd>{timingLabel(selected)}</dd>
              </div>
              <div className="def-row">
                <dt>Composed by</dt>
                <dd>{selected.created_by ?? 'not recorded'}</dd>
              </div>
              <div className="def-row">
                <dt>Composed at</dt>
                <dd>{when(selected.created_at)}</dd>
              </div>
            </dl>

            <span className="panel-title">Copy per locale</span>
            <div className="stack">
              <div className="notice notice-quiet">
                <Icon name="translate" />
                <div>
                  <strong>English (PH)</strong>
                  <div>{localeCopy(selected, 'en-PH') ?? 'Not written.'}</div>
                </div>
              </div>
              <div className="notice notice-quiet">
                <Icon name="translate" />
                <div>
                  <strong>Filipino</strong>
                  <div>{localeCopy(selected, 'fil-PH') ?? 'Not written.'}</div>
                </div>
              </div>
              {hasBothLocales(selected) ? null : (
                <div className="notice" id="announcement-locale-gap">
                  <Icon name="warning-circle" />
                  <div>
                    Both locales are required to publish, so this can be neither scheduled nor
                    sent as it stands — scheduling and sending both refuse it, naming the locale
                    that is missing.
                    They travel together in one message: no player record names a language, so
                    choosing one silently would make the other requirement decorative. Nothing
                    UNDERNEATH enforces it:{' '}
                    the body is stored as free-form content with nothing checking which locales
                    it carries, so the checks at scheduling and sending are the only guard — a
                    change to the stored copy made any other way would get through.{' '}
                    <strong>Rewrite copy</strong> on this drawer fills the missing locale in —{' '}
                    a merge
                    that leaves the locale already written alone.
                  </div>
                </div>
              )}
            </div>

            <span className="panel-title">Delivery report</span>
            {(() => {
              const d = deliveryFor(selected.ref);
              if (deliveriesFailed) {
                return (
                  <div className="notice">
                    <Icon name="warning-circle" />
                    <div>
                      The delivery record could not be read, so this says nothing about{' '}
                      {selected.ref} — not that nothing was sent. Reload to ask again.
                    </div>
                  </div>
                );
              }
              if (loadingDeliveries) {
                return (
                  <div className="notice notice-quiet">
                    <Icon name="hourglass" />
                    <div>Reading the delivery record…</div>
                  </div>
                );
              }
              if (!d) {
                /*
                 * TWO DIFFERENT ZEROES, and conflating them would put a
                 * contradiction on the screen.
                 *
                 * A DRAFT with no delivery record has simply not been sent. A
                 * row already marked SENT with no delivery record is a send
                 * that happened before anything recorded one — every
                 * announcement the seed wrote is in exactly that state — and
                 * telling an operator it "has never been sent" beside a Sent
                 * status would be the console arguing with itself.
                 *
                 * The evidence for the draft's zero is that `announcement_delivery`
                 * holds no row for it, which is what the sentence used to say.
                 * It says "no delivery has been recorded" now: the claim is the
                 * same one, and the operator cannot open a table to check the
                 * old wording — the drawer one line up already calls this thing
                 * a delivery record, so that is the phrase they have.
                 */
                return (
                  <div className="notice notice-quiet">
                    <Icon name="chart-bar" />
                    <div>
                      {selected.status === 'SENT' ? (
                        <>
                          {selected.ref} is marked Sent and carries no delivery record. It was sent
                          before this platform recorded who an announcement reached, so nothing here
                          can say who received it — that is missing evidence, not a reach of zero.
                        </>
                      ) : (
                        <>
                          {selected.ref} has not been sent, so nobody has been written to. This is a
                          real zero: no delivery has been recorded for it.
                        </>
                      )}
                    </div>
                  </div>
                );
              }
              return (
                <div className="notice notice-quiet" id="announcement-delivery">
                  <Icon name="chart-bar" />
                  <div>
                    <div>
                      Written to <strong className="tabular">{formatCount(d.recipients)}</strong>{' '}
                      {d.recipients === 1 ? 'person' : 'people'} ·{' '}
                      <span className="tabular">{formatCount(d.delivered)}</span> delivered ·{' '}
                      <span className="tabular">{formatCount(d.in_flight)}</span> still queued ·{' '}
                      <span className="tabular">{formatCount(d.suppressed)}</span> suppressed ·{' '}
                      <span className="tabular">{formatCount(d.retrying)}</span> retrying ·{' '}
                      <span className="tabular">{formatCount(d.dead)}</span> dead
                    </div>
                    <div className="text-subtle">
                      Every figure is a message status in{' '}
                      the mail outbox, moved by the mail
                      dispatcher. There is no opened or tapped figure and there never was one: this
                      platform has no open tracking of any kind, so a percentage here would be
                      invented, and an invented open rate is a figure a marketer will spend a budget
                      against.
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : null}
      </Drawer>

      {/* ── Delivery reports (LISTS.delivery) ─────────────────────────────── */}
      <Dialog
        open={dialog === 'delivery'}
        onClose={() => setDialog(null)}
        title="Delivery reports"
        wide
        actions={<Button onClick={() => setDialog(null)}>Close</Button>}
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              One row per announcement that has been sent, counted from{' '}
              the delivery record and the outbox it
              points at. <strong>Opens and taps are not shown</strong>, and their absence
              is the honest answer rather than an empty one: this platform has no open tracking of
              any kind — no pixel, no click wrapper, no bounce-and-open event subscription — so a
              percentage here would be invented, and an invented open rate is a figure a marketer
              will spend a budget against. Email is also the only channel with a transport, so
              there is nothing to break down per channel either.
            </div>
          </div>
          {deliveriesFailed ? (
            <EmptyState icon="warning-circle" title="The delivery record could not be read">
              The delivery records did not come
              back, so this table is empty because nothing was read — not because nothing was sent.
            </EmptyState>
          ) : (
            <DataTable
              caption="Announcements that have been sent, with the recipients written to and what the outbox did with each message"
              columns={deliveryColumns}
              rows={deliveries}
              rowKey={(d) => d.id}
              empty={
                <EmptyState icon="chart-bar" title="Nothing has been sent yet">
                  No announcement in {brandLabel} has been sent, so no delivery record exists. This
                  is a real zero — the list is read live, and pressing Send is the only thing that
                  adds to it.
                </EmptyState>
              }
            />
          )}
        </div>
      </Dialog>

      {/* ── SENDING, and the number it is confirmed against ─────────────────
          The primary action is disabled until the audience has been counted and
          until nothing blocks the send, and each disabled state carries its own
          reason. A confirmation whose button works before the count arrives is
          a confirmation of nothing. */}
      {/*
        `dismissible={false}` BECAUSE THIS IS THE ONE PRESS NOBODY CAN TAKE BACK.
        Every other confirmation in this console can be undone by doing the
        opposite; a send cannot, because the people who received it have it. A
        stray Escape here does no harm — but the habit it builds is the problem,
        and this is the dialog where the habit must not be "press keys until it
        goes away". It was freely dismissible, and the rule that should have
        caught that could not see the word "send".
      */}
      <Dialog
        open={sending !== null}
        onClose={() => setSending(null)}
        dismissible={false}
        title={sending ? `Send ${sending.ref}?` : 'Send announcement'}
        wide
        actions={
          <>
            <Button onClick={() => setSending(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={
                saving ||
                sendAudience === null ||
                sendAudience.recipients === 0 ||
                sendBlockers.length > 0
              }
              aria-label={
                sendAudience === null
                  ? 'Send — the audience has not been counted, and the send is confirmed against the count'
                  : sendBlockers.length > 0
                    ? `Send — ${sendBlockers[0]!.detail}`
                    : sendAudience.recipients === 0
                      ? 'Send — this audience resolves to nobody'
                      : undefined
              }
              onClick={() => void submitSend()}
            >
              {saving
                ? 'Sending…'
                : sendAudience
                  ? `Send to ${formatCount(sendAudience.recipients)}`
                  : 'Send'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {sending ? (
            <div className="notice notice-danger">
              <Icon name="warning" />
              <div>
                <p>
                  <strong>{announcementTitle(sending)}</strong> goes out as{' '}
                  {sending.kind === 'PROMOTIONAL'
                    ? 'a promotional message'
                    : 'an operational notice'}{' '}
                  to {sending.brand ?? 'every brand'}, by <strong>email</strong>, and starts
                  arriving within the minute.
                </p>
                <p>
                  <strong>This cannot be called off once pressed.</strong> There is no unsend:
                  everybody it reaches keeps it, and the only correction is a second announcement
                  saying so. Check the number below before you press.
                </p>
              </div>
            </div>
          ) : null}

          <AudienceSummary
            audience={sendAudience}
            counting={countingAudience}
            failed={countFailed}
            kind={sending?.kind ?? ''}
          />

          {sendBlockers.map((b) => (
            <div className="notice" key={b.code}>
              <Icon name="warning-circle" />
              <div>{b.detail}</div>
            </div>
          ))}

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              The number above goes with your press, and the platform counts again at the moment
              of sending. If the audience has moved since — somebody self-excluded, a bonus
              expired, an account was created — <strong>nothing is sent</strong> and you are asked to
              confirm the new number. A send cannot be undone: the people who receive it have it,
              and a correction has to be a new announcement.
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Queuing a draft ────────────────────────────────────────────────── */}
      <Dialog
        open={scheduling !== null}
        onClose={() => setScheduling(null)}
        title={scheduling ? `Schedule ${scheduling.ref}?` : 'Schedule announcement'}
        actions={
          <>
            <Button onClick={() => setScheduling(null)}>Cancel</Button>
            <Button variant="primary" disabled={saving} onClick={() => void submitSchedule()}>
              {saving ? 'Queuing…' : 'Schedule the send'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="broadcast" />
            <div>
              {scheduling ? (
                <>
                  <strong>{announcementTitle(scheduling)}</strong> goes to{' '}
                  {audienceLabel(scheduling)} over {channelLabel(scheduling.channels).toLowerCase()}.{' '}
                  {optOutLabel(scheduling.kind)}.{' '}
                </>
              ) : null}
              The audience rule is <strong>resolved when the send runs</strong>, not now, so no
              count is shown here — a player who self-excludes between this moment and that one is
              not in it. Scheduling also fires nothing by itself: there is no scheduler job, so a
              queued send waits at <strong>Scheduled</strong> until somebody presses{' '}
              <strong>Send now</strong> from the row&rsquo;s drawer, and that is where the recipient
              count is shown and confirmed.
            </div>
          </div>

          <Field
            label="Send at (PHT)"
            hint="Philippine time, the clock every timestamp on this screen is printed in."
            error={sendAtError}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                type="datetime-local"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            )}
          </Field>

          {/*
           * The field that stays empty is `sent_at`, for the same reason given
           * on the list above. It is called the sent time here because that is
           * what the operator will go and look at — the Timing line in the
           * drawer, and the Sent column on the delivery report. The warning is
           * unchanged; only the name it used is gone.
           */}
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              {/* WAS: "Nothing in the platform delivers an announcement yet."
                  That was true when scheduling was all there was, and the send
                  route has delivered to real inboxes since. Left standing, it
                  told an operator the platform was inert — next to a Send now
                  button that mails everybody. The half that is still true is
                  the SCHEDULER: no job fires a queued send, which is why a
                  Scheduled row sits past its moment until somebody presses. */}
              This queues the send; nothing fires it automatically. There is no job watching the
              clock, so the row will read Scheduled and its sent time will stay empty even after
              the moment passes — it goes out when somebody presses <strong>Send now</strong>,
              and then it really does go out. The send can be called off from this table until
              then.
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── Calling a queued send off ──────────────────────────────────────── */}
      <Dialog
        open={cancellingSend !== null}
        onClose={() => setCancellingSend(null)}
        title={cancellingSend ? `Cancel the send on ${cancellingSend.ref}?` : 'Cancel send'}
        /*
         * NOT DISMISSIBLE, AND THE POINT IS *WHEN* IT IS NOT.
         *
         * This dialog costs the operator something to fill in: a reason from
         * the list and a note of at least ten characters, written for the next
         * person who asks why the send did not go. A stray Escape — or a click
         * on the backdrop while reaching for the note field — throws that away
         * silently and takes the decision with it, and nothing on the screen
         * afterwards says a decision was abandoned rather than never made.
         *
         * Note what this is NOT. Several dialogs in the console wrote
         * `dismissible={!saving}`, which reads as caution and is the guard on
         * the wrong side of the button: it holds Escape shut only while the
         * write is already in flight, which is the one moment the decision has
         * been made and the dialog is about to close itself anyway. The moment
         * worth protecting is before the press, so the answer is a flat false.
         * Leaving is still one click on "Keep it queued", which is also the
         * honest label for what leaving does.
         */
        dismissible={false}
        actions={
          <>
            <Button onClick={() => setCancellingSend(null)}>Keep it queued</Button>
            <Button
              variant="primary"
              disabled={cancelNote.trim().length < 10 || saving}
              onClick={() => void submitCancel()}
            >
              {saving ? 'Cancelling…' : 'Cancel the send'}
            </Button>
          </>
        }
      >
        <div className="stack">
          {/*
           * WARN, NOT DANGER — AND THE ANSWER IS IN WHAT THE ROUTE DOES.
           *
           * The contract written beside these two classes reserves danger for
           * the irreversible, and for anything that moves money or a figure
           * somebody reports. Calling off a queued send is neither. Nothing has
           * reached anybody: a send that already went is refused outright and
           * told to be a new announcement instead, so by the time this dialog
           * opens there is a queued send and nothing else. No player hears of
           * this, no balance moves, and no report changes. The announcement
           * goes back to Draft whole and the Schedule button on its drawer
           * queues it again, which is exactly the reversible case warn was
           * added for.
           *
           * Spending danger here would be the expensive mistake. This is the
           * mildest write on the screen, and the screen also holds the one that
           * mails a few thousand people and cannot be taken back. If both wear
           * the same red, the red has stopped saying anything by the time an
           * operator reaches the one that matters.
           *
           * The prose is the paragraph that was already in this dialog, carried
           * across rather than rewritten, because it was already saying the
           * right thing — the tone was all that was missing. One clause changed:
           * it used to finish by naming a column on a table, which is the
           * database's shape and none of an operator's business. What it was
           * reaching for is worth keeping, though, so it now says the same
           * thing in the terms somebody hunting for this next month would use:
           * the announcement carries no mark of the cancellation, and the audit
           * trail is where it shows.
           */}
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              What is cancelled is the SEND, not the announcement.{' '}
              {cancellingSend?.ref ?? 'It'} goes back to Draft with its copy, audience and channels
              intact and its time dropped, so a send called off because the timing changed can be
              queued again rather than rewritten. The reason you give is recorded on the audit
              trail, which is where the cancellation shows afterwards — the announcement itself
              carries no mark of it.
            </div>
          </div>

          <Field label="Reason" hint="A closed set, so cancelled sends can be counted by cause.">
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                {CANCEL_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            hint="At least 10 characters. A code on its own reads as a form number; this is what the next person to ask will read."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Reload promo moved to the following weekend."
              />
            )}
          </Field>
        </div>
      </Dialog>

      <ComposeAnnouncementDialog
        open={dialog === 'compose'}
        onClose={closeCompose}
        onDone={() => setWriteCount((n) => n + 1)}
        brands={brands}
        defaultBrandId={brandId}
        namedPlayerIds={handedOverPlayers ?? undefined}
      />

      <RewriteCopyDialog
        draft={rewriting}
        onClose={() => setRewriting(null)}
        onDone={() => setWriteCount((n) => n + 1)}
      />

      {/* RE-READ, never patch: the route can refuse the move for a reason this
          screen did not predict, and the row carries who changed it and why —
          neither of which the client can compute. */}
      <NotificationSwitchDialog
        setting={switching}
        onClose={() => setSwitching(null)}
        onDone={() => setSettingWrites((n) => n + 1)}
      />
    </>
  );
}
